import React from "react";

/**
 * HidroMark — the brand wordmark. Switches between light and dark renderings
 * based on `tone`. Larger by default so the brand is immediately legible.
 *
 * Props:
 *   tone: "light" (white logo for dark backgrounds) | "dark" (dark logo for light backgrounds) | "auto"
 *   size: numeric height in px of the wordmark glyph (default 52)
 */
export const HidroMark = ({ size = 52, tone = "light", className = "" }) => {
  // tone="light" → white wordmark for dark backgrounds (hero photography, dark sections)
  // tone="dark"  → branded coloured wordmark (teal "Hidro" + grey "Verse" + droplet) for light backgrounds.
  //                Sourced from `hidroverse_alt6.png` which is the full horizontal colour-branded version.
  const src =
    tone === "light"
      ? "/brand/hidroverse_wordmark_light.webp"
      : "/brand/hidroverse_alt6.webp";

  // A subtle drop-shadow ensures legibility on busy hero photography
  // (light wordmark on bright sky, dark wordmark on light grain, etc.)
  const shadow =
    tone === "light"
      ? "drop-shadow(0 2px 8px rgba(0,0,0,0.55)) drop-shadow(0 1px 2px rgba(0,0,0,0.35))"
      : "drop-shadow(0 1px 2px rgba(255,255,255,0.4))";

  return (
    <img
      src={src}
      alt="HidroVerse — Universal Water Systems"
      className={className}
      style={{
        height: size * 1.2,
        width: "auto",
        display: "block",
        objectFit: "contain",
        filter: shadow,
        transition: "filter 220ms ease, opacity 220ms ease",
      }}
      draggable={false}
      data-testid="brand-wordmark" loading="lazy" decoding="async"/>
  );
};

/**
 * HidroWordmark — convenience wrapper used by Nav and Footer.
 * `compact` keeps the older smaller size where intended (rare).
 */
export const HidroWordmark = ({ tone = "light", compact = false, size }) => {
  const finalSize = size ?? (compact ? 38 : 52);
  return <HidroMark size={finalSize} tone={tone} />;
};

export default HidroMark;
